// What the approve screen opens with, and what it hands back when the user leaves it.
//
// Every field on that screen is local component state until Approve, and the screen unmounts the
// moment the user taps back to the camera. The label was written back to the queued scan on the way
// out and nothing else was, so a count, a tag, a chosen destination, a dropped reference frame or an
// unchecked compartment was silently discarded. It reads as a multi-scan bug because a queue is the
// only reason to leave this screen without approving: with one scan you approve and never find out.
//
// The label lives on the tap itself as well as in the draft, because the queue strip along the bottom
// of the camera draws `tap.label` — the pill has to show the name the user typed, not the one the model
// proposed and they rejected.

// The scan's own proposals, for a tap nobody has edited yet. Split out because the retry path needs
// exactly this half back.
function proposals(tap) {
  return {
    label: tap.label || "",
    // The camera's location | item toggle is a statement, not a guess, so it wins where it exists.
    // `is_location_guess` remains the fallback for a tap captured before that toggle existed.
    isLocation: tap.capturedAsLocation ?? !!tap.is_location_guess,
    tags: Array.isArray(tap.tags) ? tap.tags : [],
    predictedSublocations: (Array.isArray(tap.predicted_sublocations) ? tap.predicted_sublocations : [])
      .map((label, i) => ({ id: `${tap.id}-sl-${i}`, label, checked: true })),
    trail: (Array.isArray(tap.trail) ? tap.trail : []).map((url, i) => ({ id: `${tap.id}-trail-${i}`, url })),
  };
}

// Answers "what should the fields say" for one queued scan: the user's own edits if they have been
// here before, otherwise what the scan arrived with.
export function verifyDraft(tap) {
  if (!tap) return null;
  const stashed = tap.draft;
  if (!stashed) {
    return {
      ...proposals(tap),
      // Not proposals — nobody suggests these, so their untouched value is their default.
      locOverride: null,
      scanMoreHere: false,
      // A string so the field can be emptied while typing without snapping back to 1 under the cursor.
      quantity: "1",
    };
  }
  return stashed;
}

// A retry re-asks the model, so the model's fresh answer has to win over the one the user was looking
// at — otherwise reopening the scan shows the label they just replaced. What they typed themselves is
// theirs and survives: nothing about asking again says the count was wrong.
export function draftAfterRetry(tap, draft) {
  if (!draft) return null;
  return {
    ...draft,
    ...proposals(tap),
  };
}

// Whether a handled scan's form now says something different from what was stored for it.
//
// Only the fields that can still be changed once an item exists are compared, because those are the only
// ones the screen may offer: the reference frames and the compartment predictions are spent by the save,
// and flipping a saved item into a location is a conversion rather than an edit. Anything this ignores
// must not be editable on that screen either — a field you can type into that changes nothing is the
// thing this whole path exists to stop.
export function draftChanged(saved, current) {
  if (!saved || !current) return false;
  if (String(saved.label ?? "").trim() !== String(current.label ?? "").trim()) return true;
  // `|| 1` because the count field can legitimately be empty mid-typing, and the save reads that as 1.
  if ((Number(saved.quantity) || 1) !== (Number(current.quantity) || 1)) return true;
  // Order is not a difference: the picker appends and the chips can be removed from the middle.
  const tagKey = (t) => (Array.isArray(t) ? [...t].sort().join("\u0000") : "");
  if (tagKey(saved.tags) !== tagKey(current.tags)) return true;
  if ((saved.locOverride?.id ?? null) !== (current.locOverride?.id ?? null)) return true;
  return false;
}

// The form as it was at the moment Approve was pressed, rebuilt from what that press sent.
//
// A draft is only stashed when the user *leaves* this screen, so someone who edited and then approved
// straight away had their edits travel in `opts` to the save and nowhere else. Reopening that scan from
// the session history then showed the model's original label, a count of 1 and no chosen destination —
// the edits were in the database and not on the screen, which read as the history losing them.
//
// This is the inverse of what `Verify` sends, so the two shapes have to be kept in step: `opts` carries
// a number for the count and bare strings for the trail and the compartments, while the form holds a
// string and objects with ids. Anything `opts` does not mention keeps what the scan proposed.
export function draftFromApprove(tap, finalLabel, opts = {}) {
  const base = verifyDraft(tap);
  if (!base) return null;
  return {
    ...base,
    label: finalLabel ?? base.label,
    isLocation: !!opts.isLocation,
    // A string, because that is what the count field holds — a number here makes the input fight the
    // user the moment they try to clear it.
    quantity: String(opts.quantity ?? base.quantity ?? "1"),
    scanMoreHere: !!opts.scanMoreHere,
    tags: Array.isArray(opts.tags) ? opts.tags : base.tags,
    // No override means it went where it was scanned, which is the tap's own stamp — so null is the
    // right answer rather than a missing one.
    locOverride: opts.overrideLocationId
      ? { id: opts.overrideLocationId, name: opts.overrideLocationName || "" }
      : base.locOverride,
    trail: Array.isArray(opts.trail)
      ? opts.trail.map((url, i) => ({ id: `${tap?.id}-trail-${i}`, url }))
      : base.trail,
    // Only the ones that were ticked were sent, so the rest are gone rather than unticked. Showing them
    // again unticked would suggest they are still on offer, and approving is over.
    predictedSublocations: Array.isArray(opts.predictedSublocations)
      ? opts.predictedSublocations.map((label, i) => ({ id: `${tap?.id}-sl-${i}`, label, checked: true }))
      : base.predictedSublocations,
  };
}
